import { styled } from "goober";
import { useState } from "react";
import { toggleComposerStrip } from "../../hooks/useComposerStrip";
import { Icon } from "../Icon";
import { spin } from "../motion";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";

const Row = styled("div")`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const RetryAction = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 9999px;
  background: transparent;
  color: var(--text-primary);
  &:hover {
    background-color: var(--alpha4);
  }
`;

const ModelPickerButton = styled(PressButton)`
  color: var(--text-secondary);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    color: var(--text-primary);
  }
`;

const RetryIcon = styled("span")`
  display: flex;

  &[data-spinning="true"] {
    animation: ${spin} 450ms var(--ease-out);
  }
`;

export function ErrorMessage({
  title,
  message,
  createdAt,
  canRetry,
  onRetry,
  suggestModelSwitch,
}: {
  title: string;
  message: string;
  createdAt: number;
  canRetry: boolean;
  onRetry: () => void;
  suggestModelSwitch: boolean;
}) {
  // Expanded when the error just happened (arrived live while the user is
  // watching); collapsed when revisiting an older conversation.
  const [expanded, setExpanded] = useState(
    () => Date.now() - createdAt < 10_000
  );
  const [retrySpinKey, setRetrySpinKey] = useState(0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Row>
        <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
          <CollapsibleHeaderInner>
            <ToggleIconContainer aria-hidden>
              <DefaultIcon>
                <Icon
                  name="traffic-cone"
                  size={12}
                  color="var(--text-secondary)"
                />
              </DefaultIcon>
              <HoverIcon>
                <Icon
                  name={expanded ? "minus" : "plus"}
                  size={12}
                  color="var(--text-secondary)"
                />
              </HoverIcon>
            </ToggleIconContainer>
            <Text variant="text-small" color="var(--text-secondary)">
              {title}
            </Text>
          </CollapsibleHeaderInner>
        </CollapsibleHeader>
        {canRetry && (
          <Tooltip content="Retry">
            <RetryAction
              type="button"
              aria-label="Retry"
              onPress={() => {
                setRetrySpinKey((key) => key + 1);
                onRetry();
              }}
            >
              <RetryIcon key={retrySpinKey} data-spinning={retrySpinKey > 0}>
                <Icon name="rotate-cw" size={12} />
              </RetryIcon>
            </RetryAction>
          </Tooltip>
        )}
      </Row>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {message}
              </span>
            </Text>
            {suggestModelSwitch && (
              <div style={{ marginTop: 8 }}>
                <Text variant="text-small" color="var(--text-secondary)">
                  If this keeps happening, try switching to a different model in
                  the{" "}
                  <ModelPickerButton onPress={toggleComposerStrip}>
                    model picker
                  </ModelPickerButton>
                  .
                </Text>
              </div>
            )}
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}
